import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { UserPlus, Shield, Trash2, Power, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function SuperAdminManagePage() {
    const navigate = useNavigate();
    const [admins, setAdmins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showDialog, setShowDialog] = useState(false);
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        password: ""
    });

    const token = localStorage.getItem('superadmin_token');

    useEffect(() => {
        if (!token) {
            navigate('/superadmin/login');
            return;
        }
        loadAdmins();
    }, [token]);

    const loadAdmins = async () => {
        try {
            const response = await axios.get(`${API_BASE}/api/superadmin/admins`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setAdmins(response.data);
        } catch (error) {
            toast.error("Failed to load admins");
        } finally {
            setLoading(false);
        }
    };

    const handleCreateAdmin = async (e) => {
        e.preventDefault();

        try {
            await axios.post(`${API_BASE}/api/superadmin/create-admin`, formData, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success("Admin created successfully");
            setShowDialog(false);
            setFormData({ name: "", email: "", password: "" });
            loadAdmins();
        } catch (error) {
            toast.error(error.response?.data?.detail || "Failed to create admin");
        }
    };

    const handleToggleStatus = async (adminId) => {
        try {
            await axios.put(`${API_BASE}/api/superadmin/admins/${adminId}/toggle`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success("Admin status updated");
            loadAdmins();
        } catch (error) {
            toast.error(error.response?.data?.detail || "Failed to update status");
        }
    };

    const handleDeleteAdmin = async (adminId, adminName) => {
        if (!confirm(`Are you sure you want to delete ${adminName}?`)) {
            return;
        }

        try {
            await axios.delete(`${API_BASE}/api/superadmin/admins/${adminId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success("Admin deleted successfully");
            loadAdmins();
        } catch (error) {
            toast.error(error.response?.data?.detail || "Failed to delete admin");
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background p-4 md:p-8">
            <div className="max-w-4xl mx-auto space-y-6">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Button variant="ghost" onClick={() => navigate('/superadmin')}>
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            Back
                        </Button>
                        <div>
                            <h1 className="font-heading text-3xl font-bold">Admin Management</h1>
                            <p className="text-muted-foreground">Manage SuperAdmin users</p>
                        </div>
                    </div>

                    <Dialog open={showDialog} onOpenChange={setShowDialog}>
                        <DialogTrigger asChild>
                            <Button>
                                <UserPlus className="w-4 h-4 mr-2" />
                                Add Admin
                            </Button>
                        </DialogTrigger>
                        <DialogContent>
                            <DialogHeader>
                                <DialogTitle>Create New SuperAdmin</DialogTitle>
                            </DialogHeader>
                            <form onSubmit={handleCreateAdmin} className="space-y-4">
                                <div className="space-y-2">
                                    <Label>Full Name</Label>
                                    <Input
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        required
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Email</Label>
                                    <Input
                                        type="email"
                                        value={formData.email}
                                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                        required
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Password</Label>
                                    <Input
                                        type="password"
                                        value={formData.password}
                                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                        required
                                        minLength={8}
                                    />
                                </div>
                                <Button type="submit" className="w-full">Create Admin</Button>
                            </form>
                        </DialogContent>
                    </Dialog>
                </div>

                <Card>
                    <CardHeader>
                        <CardTitle>SuperAdmin Users</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-3">
                            {admins.map((admin) => (
                                <div key={admin.id} className="flex items-center justify-between p-4 border rounded-lg">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                                            <Shield className="w-5 h-5 text-primary" />
                                        </div>
                                        <div>
                                            <p className="font-medium">{admin.name}</p>
                                            <p className="text-sm text-muted-foreground">{admin.email}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Badge variant={admin.is_active ? "default" : "secondary"}>
                                            {admin.is_active ? "Active" : "Inactive"}
                                        </Badge>
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            onClick={() => handleToggleStatus(admin.id)}
                                        >
                                            <Power className="w-4 h-4" />
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            onClick={() => handleDeleteAdmin(admin.id, admin.name)}
                                        >
                                            <Trash2 className="w-4 h-4 text-destructive" />
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}